import { useCallback, useEffect, useState } from "react";
import "./CancelRefundReturn.css";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";

const STATUS_LABEL = {
  WAITING_RETURN: "Menunggu Retur Fisik",
  RETURN_IN_TRANSIT: "Menunggu Retur Fisik",
  PHYSICAL_COMPLETED: "Selesai / Resi Retur Sudah Kembali",
};
const RESULT_CLASS = { CLOSED: "badge on", WILL_CLOSE: "badge on", SKIP: "badge", HOLD: "badge" };
const OUTSTANDING_PAGE_SIZE = 10;

export default function CancelRefundReturn() {
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";
  const [stores, setStores] = useState([]);
  const [cases, setCases] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [imp, setImp] = useState({ file: null, preview: null, committed: null });
  const [filter, setFilter] = useState({ status: "ALL", store_id: "", marketplace: "ALL", search: "" });
  const [casePage, setCasePage] = useState(1);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [actionsCaseId, setActionsCaseId] = useState(null);

  const loadCases = useCallback(() => {
    const params = {};
    if (filter.status && filter.status !== "ALL") params.status = filter.status;
    if (filter.store_id) params.store_id = filter.store_id;
    if (filter.marketplace && filter.marketplace !== "ALL") params.marketplace = filter.marketplace;
    if (filter.search && filter.search.trim()) params.search = filter.search.trim();
    api.get("/cancel-refund/cases", { params }).then(({ data }) => setCases(data)).catch(() => {});
  }, [filter]);

  useEffect(() => { if (isOwner) api.get("/stores").then(({ data }) => setStores(data)).catch(() => {}); }, [isOwner]);
  useEffect(() => { if (isOwner) loadCases(); }, [isOwner, loadCases]);

  // Presentation only: the backend supplies all rows matching the active filters.
  // Pagination never truncates the existing export request or changes API parameters.
  const updateCaseFilter = (key, value) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
    setCasePage(1);
    setSelectedCaseId(null);
    setActionsCaseId(null);
  };
  const resetCaseFilters = () => {
    setFilter({ status: "ALL", store_id: "", marketplace: "ALL", search: "" });
    setCasePage(1);
    setSelectedCaseId(null);
    setActionsCaseId(null);
  };
  const totalCasePages = Math.max(1, Math.ceil(cases.length / OUTSTANDING_PAGE_SIZE));
  const currentCasePage = Math.min(casePage, totalCasePages);
  const visibleCases = cases.slice(
    (currentCasePage - 1) * OUTSTANDING_PAGE_SIZE,
    currentCasePage * OUTSTANDING_PAGE_SIZE
  );
  const firstPageNumber = Math.max(1, Math.min(currentCasePage - 2, totalCasePages - 4));
  const casePageNumbers = Array.from(
    { length: Math.min(5, totalCasePages) },
    (_, i) => firstPageNumber + i
  );
  const selectedCase = cases.find((c) => c.id === selectedCaseId);
  const changeCasePage = (nextPage) => {
    setCasePage(Math.max(1, Math.min(nextPage, totalCasePages)));
    setSelectedCaseId(null);
    setActionsCaseId(null);
  };

  const doPreview = async () => {
    if (!imp.file) return;
    setError(""); setNotice(""); setBusy("preview");
    try {
      const fd = new FormData(); fd.append("file", imp.file);
      const { data } = await api.post("/cancel-refund/import-preview", fd);
      setImp((s) => ({ ...s, preview: data, committed: null }));
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Preview gagal."); }
    finally { setBusy(""); }
  };

  const exportFiltered = async () => {
    if (!cases.length) return;
    try {
      const params = {};
      if (filter.status && filter.status !== "ALL") params.status = filter.status;
      if (filter.store_id) params.store_id = filter.store_id;
      if (filter.marketplace && filter.marketplace !== "ALL") params.marketplace = filter.marketplace;
      if (filter.search && filter.search.trim()) params.search = filter.search.trim();
      const { data } = await api.get("/cancel-refund/cases/export", { params, responseType: "blob" });
      const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "outstanding_retur_followup.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let message = formatApiErrorDetail(e.response?.data?.detail);
      const data = e.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          message = formatApiErrorDetail(parsed?.detail ?? parsed);
        } catch (_) {
          // Keep the generic message when the response is not JSON.
        }
      }
      setError(message || "Export Excel gagal.");
    }
  };

  const closeCaseReceivable = async (caseId) => {
    setError(""); setNotice(""); setBusy(`close-${caseId}`);
    try {
      const { data } = await api.post(`/cancel-refund/cases/${caseId}/close-receivable`);
      setNotice(data.reason === "ALREADY_CLOSED" ? "Piutang sudah CLOSED." : "Piutang berhasil ditutup.");
      loadCases();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Piutang belum dapat ditutup."); }
    finally { setBusy(""); }
  };

  const doCommit = async () => {
    if (!imp.file) return;
    setError(""); setNotice(""); setBusy("commit");
    try {
      const fd = new FormData(); fd.append("file", imp.file);
      const { data } = await api.post("/cancel-refund/import", fd);
      setImp((s) => ({ ...s, committed: data, preview: null }));
      setNotice(`Commit selesai: ${data.summary.CLOSED} piutang ditutup → MENUNGGU RETUR FISIK, ${data.summary.HOLD} HOLD, ${data.summary.SKIP} skip.`);
      loadCases();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Commit gagal."); }
    finally { setBusy(""); }
  };

  if (!isOwner) {
    return <div className="app-shell"><AppTopBar active="cancel-refund" /><main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main></div>;
  }

  const resultTable = (data, testid) => (
    <div className="table-scroll" style={{ marginTop: 14 }}>
      <table className="data-table" data-testid={testid}>
        <thead><tr><th>Order ID</th><th>Store</th><th>Hasil</th><th>Keterangan</th></tr></thead>
        <tbody>
          {data.results.map((r, i) => (
            <tr key={`${r.order_id}-${i}`} data-testid={`crr-import-row-${r.order_id}`}>
              <td className="mono">{r.order_id}</td><td>{r.store}</td>
              <td><span className={RESULT_CLASS[r.result] || "badge"}>{r.result}</span></td>
              <td className="hint">{r.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="app-shell">
      <AppTopBar active="cancel-refund" />
      <main className="content">
        <div style={{ marginBottom: 18 }}>
          <h1 style={{ margin: "0 0 4px", fontSize: 24 }}>Cancel / Refund Return</h1>
          <p className="hint" style={{ margin: 0 }}>Import file cancel/refund yang sudah Anda validasi. Commit menutup piutang (CLOSED_REFUND_CANCEL) dan memunculkan order di daftar outstanding retur di bawah. Penerimaan barang tetap lewat Retur Pembeli.</p>
        </div>
        {error && <div className="alert-error" data-testid="crr-error">{error}</div>}
        {notice && <div className="alert-info" data-testid="crr-notice">{notice}</div>}

        {/* IMPORT VALIDATED (Preview -> Commit) */}
        <section className="panel panel--wide" data-testid="crr-import-panel" style={{ marginBottom: 18 }}>
          <h2 className="panel-title">Import Cancel / Refund Validated</h2>
          <div className="hint" style={{ marginBottom: 10 }}>Gunakan file hasil <b>Export Cancel/Refund dari ERP</b> (format Settlement Export) yang sudah Anda validasi/sortir di Ginee — upload file yang sama (.xlsx atau .csv), tanpa template baru. Auto-match ke piutang berdasarkan Store + Order ID. Idempotent.</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ minWidth: 260, flex: 1 }}>
              <label className="field-label">File Export Cancel/Refund ERP (.xlsx / .csv)</label>
              <input className="field-input" type="file" accept=".xlsx,.csv" onChange={(e) => setImp({ file: e.target.files[0], preview: null, committed: null })} data-testid="crr-import-file" />
            </div>
            <button className="btn-ghost" onClick={doPreview} disabled={!imp.file || busy === "preview"} data-testid="crr-preview-btn">{busy === "preview" ? "Memproses…" : "Preview (auto-match)"}</button>
            <button className="btn-primary" onClick={doCommit} disabled={!imp.preview || (imp.preview?.summary?.WILL_CLOSE || 0) < 1 || busy === "commit"} style={{ opacity: (imp.preview && (imp.preview?.summary?.WILL_CLOSE || 0) >= 1 && busy !== "commit") ? 1 : 0.5, cursor: (imp.preview && (imp.preview?.summary?.WILL_CLOSE || 0) >= 1 && busy !== "commit") ? "pointer" : "not-allowed" }} data-testid="crr-commit-btn">{busy === "commit" ? "Memproses…" : "Commit — Tutup Piutang"}</button>
          </div>
          <div className="hint" style={{ marginTop: 8 }} data-testid="crr-commit-guard-hint">Commit aktif hanya setelah Preview berhasil dan ada minimal 1 baris WILL_CLOSE.</div>
          {imp.preview && (<>
            <div className="hint" style={{ marginTop: 12 }} data-testid="crr-preview-summary">Preview: {imp.preview.summary.WILL_CLOSE} akan ditutup, {imp.preview.summary.HOLD} HOLD, {imp.preview.summary.SKIP} skip.</div>
            {resultTable(imp.preview, "crr-preview-result")}
          </>)}
          {imp.committed && resultTable(imp.committed, "crr-import-result")}
        </section>


        {/* OUTSTANDING RETURN DASHBOARD — presentation only, same API/filter/export/action handlers */}
        <section className="panel panel--wide crr-dashboard" data-testid="crr-dashboard-panel">
          <header className="crr-dashboard-header">
            <div>
              <h2 className="crr-dashboard-title">Daftar Outstanding Retur</h2>
              <p className="crr-dashboard-subtitle" data-testid="crr-marketplace-count">
                Daftar retur saat ini: Shopee {cases.filter((c) => c.marketplace === "SHOPEE").length} · TikTok {cases.filter((c) => c.marketplace === "TIKTOK").length} · Unknown {cases.filter((c) => c.marketplace === "UNKNOWN").length}
              </p>
            </div>
            <button className="crr-export-button" onClick={exportFiltered} disabled={!cases.length} data-testid="crr-export-xlsx">
              Export Excel (.xlsx)
            </button>
          </header>

          <div className="crr-filter-panel">
            <div className="crr-filter-field">
              <label htmlFor="crr-status">Status</label>
              <select id="crr-status" className="field-input" value={filter.status} onChange={(e) => updateCaseFilter("status", e.target.value)} data-testid="crr-filter-status">
                <option value="ALL">Semua</option>
                <option value="WAITING">Menunggu Retur</option>
                <option value="COMPLETED">Sudah Kembali / Selesai</option>
              </select>
            </div>
            <div className="crr-filter-field">
              <label htmlFor="crr-marketplace">Marketplace</label>
              <select id="crr-marketplace" className="field-input" value={filter.marketplace} onChange={(e) => updateCaseFilter("marketplace", e.target.value)} data-testid="crr-filter-marketplace">
                <option value="ALL">Semua Marketplace</option>
                <option value="SHOPEE">Shopee</option>
                <option value="TIKTOK">TikTok</option>
                <option value="UNKNOWN">Unknown / Belum Terbukti</option>
              </select>
            </div>
            <div className="crr-filter-field">
              <label htmlFor="crr-store">Store</label>
              <select id="crr-store" className="field-input" value={filter.store_id} onChange={(e) => updateCaseFilter("store_id", e.target.value)} data-testid="crr-filter-store">
                <option value="">Semua Store</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="crr-filter-field crr-filter-search-field">
              <label htmlFor="crr-search">Cari Order ID / Return AWB</label>
              <div className="crr-search-wrap">
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none"><circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="1.7"/><path d="m15.5 15.5 5 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
                <input id="crr-search" className="field-input" value={filter.search} onChange={(e) => updateCaseFilter("search", e.target.value)} placeholder="Order ID atau Return AWB…" data-testid="crr-filter-search" />
              </div>
            </div>
            <button type="button" className="crr-reset-button" onClick={resetCaseFilters} data-testid="crr-reset-filter">Reset Filter</button>
          </div>

          <div className="crr-list-table-wrap">
            <table className="data-table crr-outstanding-table" data-testid="crr-cases-table">
              <colgroup>
                <col style={{ width: "3%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "5%" }} />
                <col style={{ width: "7%" }} />
              </colgroup>
              <thead><tr><th>#</th><th>MARKETPLACE</th><th>STORE</th><th>ORDER ID</th><th>PIUTANG</th><th>OUTBOUND AWB</th><th>RETURN AWB</th><th>STATUS RETUR</th><th>QTY (EXP/REC)</th><th>AKSI</th></tr></thead>
              <tbody>
                {cases.length === 0 && <tr><td colSpan={10} className="crr-no-data">Tidak ada data.</td></tr>}
                {visibleCases.map((c, i) => {
                  const completed = c.return_status === "PHYSICAL_COMPLETED";
                  const canClose = String(c.receivable_status || "").startsWith("OPEN");
                  return (
                    <tr key={c.id} data-testid={"crr-case-row-" + c.order_id}>
                      <td data-label="#" className="crr-row-number">{(currentCasePage - 1) * OUTSTANDING_PAGE_SIZE + i + 1}</td>
                      <td data-label="Marketplace">{c.marketplace || "UNKNOWN"}</td>
                      <td data-label="Store">{c.store || "-"}</td>
                      <td data-label="Order ID" className="crr-strong crr-wrap-text">{c.order_id || "-"}</td>
                      <td data-label="Piutang" className="crr-receivable-status crr-wrap-text">{c.receivable_status || "-"}</td>
                      <td data-label="Outbound AWB" className="crr-strong crr-wrap-text">{c.outbound_awb || "-"}</td>
                      <td data-label="Return AWB" className="crr-strong crr-wrap-text">{c.return_awb || "-"}</td>
                      <td data-label="Status Retur">
                        <span className={"crr-return-badge " + (completed ? "crr-return-complete" : "crr-return-pending")}>{completed ? "Selesai" : "Menunggu"}</span>
                        <span className="crr-return-description">{completed ? "Resi Retur Sudah Kembali" : (STATUS_LABEL[c.return_status] || c.return_status || "-")}</span>
                      </td>
                      <td data-label="Qty (Exp/Rec)" className="crr-strong">{c.expected_qty} / {c.received_qty}</td>
                      <td data-label="Aksi">
                        <div className="crr-row-actions">
                          <button type="button" className="crr-view-button" aria-expanded={selectedCaseId === c.id} onClick={() => { setSelectedCaseId(selectedCaseId === c.id ? null : c.id); setActionsCaseId(null); }} data-testid={"crr-view-" + c.order_id}>Lihat</button>
                          <div className="crr-more-wrap">
                            <button type="button" className="crr-more-button" aria-label={"Opsi " + c.order_id} aria-expanded={actionsCaseId === c.id} onClick={() => setActionsCaseId(actionsCaseId === c.id ? null : c.id)} data-testid={"crr-menu-" + c.order_id}>•••</button>
                            {actionsCaseId === c.id && (
                              <div className="crr-action-menu" role="group" aria-label={"Aksi pesanan " + c.order_id}>
                                <button type="button" onClick={() => { setSelectedCaseId(c.id); setActionsCaseId(null); }}>Lihat detail</button>
                                {canClose && <button type="button" onClick={() => { setActionsCaseId(null); closeCaseReceivable(c.id); }} disabled={busy === ("close-" + c.id)} data-testid={"crr-close-" + c.order_id}>{busy === ("close-" + c.id) ? "Memproses…" : "Tutup Piutang"}</button>}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {selectedCase && (
            <div className="crr-case-detail" data-testid="crr-case-detail">
              <div className="crr-case-detail-header">
                <strong>Detail Retur · {selectedCase.order_id}</strong>
                <button type="button" className="crr-detail-dismiss" onClick={() => setSelectedCaseId(null)} aria-label="Tutup detail">×</button>
              </div>
              <div className="crr-case-detail-grid">
                <div><span>Marketplace / Store</span><strong>{selectedCase.marketplace || "UNKNOWN"} · {selectedCase.store || "-"}</strong></div>
                <div><span>Status Piutang</span><strong>{selectedCase.receivable_status || "-"}</strong></div>
                <div><span>Outbound AWB</span><strong>{selectedCase.outbound_awb || "-"}</strong></div>
                <div><span>Return AWB</span><strong>{selectedCase.return_awb || "-"}</strong></div>
                <div><span>Status Retur</span><strong>{STATUS_LABEL[selectedCase.return_status] || selectedCase.return_status || "-"}</strong></div>
                <div><span>Qty Exp / Rec</span><strong>{selectedCase.expected_qty} / {selectedCase.received_qty}</strong></div>
              </div>
              {String(selectedCase.receivable_status || "").startsWith("OPEN") && (
                <button className="btn-mini" onClick={() => closeCaseReceivable(selectedCase.id)} disabled={busy === ("close-" + selectedCase.id)} data-testid={"crr-detail-close-" + selectedCase.order_id}>
                  {busy === ("close-" + selectedCase.id) ? "Memproses…" : "Tutup Piutang"}
                </button>
              )}
            </div>
          )}

          <footer className="crr-list-footer">
            <span data-testid="crr-pagination-count">Menampilkan {cases.length ? (currentCasePage - 1) * OUTSTANDING_PAGE_SIZE + 1 : 0} - {Math.min(currentCasePage * OUTSTANDING_PAGE_SIZE, cases.length)} dari {cases.length} data</span>
            <nav className="crr-pagination" aria-label="Halaman Outstanding Retur">
              <button type="button" aria-label="Halaman sebelumnya" disabled={currentCasePage === 1} onClick={() => changeCasePage(currentCasePage - 1)}>‹</button>
              {casePageNumbers.map((pageNum) => (
                <button type="button" key={pageNum} aria-label={"Halaman " + pageNum} aria-current={currentCasePage === pageNum ? "page" : undefined} className={currentCasePage === pageNum ? "is-active" : ""} onClick={() => changeCasePage(pageNum)}>{pageNum}</button>
              ))}
              <button type="button" aria-label="Halaman berikutnya" disabled={currentCasePage === totalCasePages} onClick={() => changeCasePage(currentCasePage + 1)}>›</button>
            </nav>
          </footer>
        </section>
      </main>
    </div>
  );
}
